import { useCallback, useEffect } from 'react';
import { useGetTcoLocations } from './apiHooks/tcoLocations/useGetTcoLocations';
import { useUpdateTcoSubmissions } from './apiHooks/tco/useUpdateTcoSubmissions';
import { useQueryData } from './useQueryData';
import { tcoFieldsIds } from 'utils/defaultsAndConstants/constants';
import { ITCOSubmission } from 'types';

interface IUseSyncNumberOfSites {
  estimateId: string;
  tcoSubmissions: ITCOSubmission[];
}

export const useSyncNumberOfSites = ({
  estimateId,
  tcoSubmissions,
}: IUseSyncNumberOfSites) => {
  const { data: tcoLocations = [] } = useGetTcoLocations({
    params: { estimateId },
  });

  const { updateTcoSubmissions } = useUpdateTcoSubmissions({ estimateId });
  const { userId } = useQueryData();

  const { numberOfSitesFieldId } = tcoFieldsIds;

  const syncNumberOfSites = useCallback(() => {
    if (!estimateId || !userId) return;

    const totalSites = tcoLocations.reduce((total, location) => {
      return total + (location.noOfSites || 1);
    }, 0);

    const existingSubmission = tcoSubmissions.find(
      (sub) => sub.fieldId === numberOfSitesFieldId
    );

    const currentValue = existingSubmission?.answer;
    if (currentValue !== totalSites.toString()) {
      const submissionData: Partial<ITCOSubmission> = {
        id: existingSubmission?.id,
        fieldId: numberOfSitesFieldId,
        answer: totalSites.toString(),
        estimateId,
        updatedBy: userId,
      };

      updateTcoSubmissions([submissionData]);
    }
  }, [
    tcoLocations,
    estimateId,
    userId,
    numberOfSitesFieldId,
    tcoSubmissions,
    updateTcoSubmissions,
  ]);

  useEffect(() => {
    syncNumberOfSites();
  }, [syncNumberOfSites]);

  return {
    syncNumberOfSites,
    totalSites: tcoLocations.reduce(
      (total, location) => total + (location.noOfSites || 1),
      0
    ),
  };
};
